import { Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WIND_ATTRIBUTION_URL, WIND_LICENCE_URL } from "@divespots/contracts";
import type { Locale } from "@divespots/contracts";
import { formatNumber } from "../../i18n/format.ts";
import { ARROW_LENGTH_PX, ARROW_WEIGHT_PX, WIND_CLASS_LIMITS_KMH, windVar } from "./ramp.ts";
import type { WindClass } from "./ramp.ts";
import { formatRunTime, formatWindTime } from "./time.ts";
import type { WindReading } from "./readout.ts";
import { precipVar } from "../precip/raster.ts";
import type { PrecipReading } from "../precip/readout.ts";
import { formatHourLabel, hourParts } from "../precip/time.ts";
import type { WindTime } from "./useWindTime.ts";

interface Props {
  time: WindTime;
  status: "loading" | "ready" | "unavailable";
  locale: Locale;
  /** Wind overlay on: its readout or arrow legend, the "Jetzt"/clock labels. Off = precipitation alone. */
  windOn?: boolean;
  /** Arrows (reduced motion) get the colour/length legend; particles get the readout. */
  mode: "particles" | "arrows";
  /** Wind at the open spot, else the map centre; `wind` null = no data there. */
  readout: { place: string; wind: WindReading | null } | null;
  /** Wind is on but its forecast failed while precipitation is shown: say so instead of going quiet. */
  windFailed?: boolean;
  /** Present while the precipitation overlay is on; `reading` null = no sum for that hour or place. */
  precip?:
    | {
        status: "loading" | "ready" | "unavailable";
        readout: { place: string; reading: PrecipReading | null } | null;
      }
    | undefined;
  /** Model run (initialisation time), when the API knows it. */
  runAt?: string | null | undefined;
}

/** Height the phone bar (and the credit under it) takes above the sheet: the pin must land above it. */
export const WIND_BAR_RESERVE_PX = 140;

/** With both overlays the bar carries a second readout row. */
export const BOTH_BAR_RESERVE_PX = 164;

/** Same for the one-line chip of landscape phones. */
export const WIND_CHIP_RESERVE_PX = 56;

const CLASS_LABELS = [
  `<${WIND_CLASS_LIMITS_KMH[0]}`,
  `${WIND_CLASS_LIMITS_KMH[0]}–${WIND_CLASS_LIMITS_KMH[1]}`,
  `${WIND_CLASS_LIMITS_KMH[1]}–${WIND_CLASS_LIMITS_KMH[2]}`,
  `≥${WIND_CLASS_LIMITS_KMH[2]}`,
] as const;

/** Glass bar under the wind overlay: forecast hour slider, speed legend, "model forecast" note. */
export function WindTimeBar({
  time,
  status,
  locale,
  runAt,
  mode,
  readout,
  windOn = true,
  windFailed = false,
  precip,
}: Props) {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const infoButton = useRef<HTMLButtonElement>(null);
  const more = useRef<HTMLDivElement>(null);
  // Esc closes the popover first: the sheet listens on window and must not also drop a detent
  useEffect(() => {
    if (!infoOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (more.current?.contains(document.activeElement)) infoButton.current?.focus();
      setInfoOpen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [infoOpen]);
  const { range, now } = time;
  const label =
    time.t === null
      ? ""
      : !windOn
        ? formatHourLabel(time.t, now, locale)
        : time.t === time.defaultT
          ? t("wind.now")
          : formatWindTime(time.t, now, locale);
  const both = windOn && !!precip;
  const prefix = both ? "precip.labelBoth" : windOn ? "wind.label" : "precip.label";
  const precipText = (() => {
    if (!precip) return null;
    if (precip.status !== "ready")
      return t(precip.status === "loading" ? "precip.loading" : "precip.unavailable");
    const r = precip.readout;
    if (!r) return null;
    if (!r.reading) return t("precip.readoutNone", { place: r.place });
    const p = hourParts(r.reading.fromMs, now, locale);
    const hours = `${p.day ? `${p.day} ` : ""}${t("precip.hours", { from: p.from, to: p.to })}`;
    return r.reading.dry
      ? t("precip.readoutDry", { place: r.place, hours })
      : t("precip.readout", { place: r.place, hours, mm: formatNumber(r.reading.mm, locale, 1) });
  })();
  const readoutText =
    !windOn || !readout
      ? null
      : readout.wind === null
        ? t("wind.readoutNone", { place: readout.place })
        : readout.wind.calm
          ? t("wind.readoutCalm", { place: readout.place })
          : t("wind.readout", {
              place: readout.place,
              kmh: readout.wind.kmh,
              dir: t(`wind.dir.${readout.wind.from}`),
            });
  return (
    <section
      className="wind-bar ds-glass"
      aria-label={t(prefix)}
      data-info={infoOpen ? "open" : "closed"}
    >
      {status === "ready" && range && time.t !== null ? (
        <>
          <span className="wind-bar__chip ds-text-caption-1">
            {label} · {t("wind.chip")}
          </span>
          <div className="wind-bar__row">
            <output className="wind-bar__time ds-text-subheadline" htmlFor="wind-time">
              {label}
            </output>
            <input
              id="wind-time"
              type="range"
              className="wind-bar__slider"
              aria-label={t("wind.time")}
              aria-valuetext={label}
              min={range.min}
              max={range.max}
              step={time.step}
              value={time.t}
              onChange={(e) => time.setT(Number(e.currentTarget.value))}
            />
            <button
              ref={infoButton}
              type="button"
              className="wind-bar__info ds-btn ds-btn--icon ds-btn--plain"
              aria-label={t(both ? "precip.infoBoth" : windOn ? "wind.info" : "precip.info")}
              aria-expanded={infoOpen}
              aria-controls="wind-more"
              onClick={() => setInfoOpen(!infoOpen)}
            >
              <Info aria-hidden="true" size={20} strokeWidth={1.75} />
            </button>
          </div>
          {windOn && mode === "arrows" ? (
            <ul className="wind-bar__legend ds-text-caption-1" aria-label={t("wind.legend")}>
              {CLASS_LABELS.map((text, c) => (
                <li key={text}>
                  <span
                    className="wind-bar__swatch"
                    aria-hidden="true"
                    style={{
                      background: `var(${windVar(c as WindClass)})`,
                      width: ARROW_LENGTH_PX[c as WindClass],
                      height: ARROW_WEIGHT_PX[c as WindClass],
                    }}
                  />
                  {text}
                </li>
              ))}
              <li className="wind-bar__unit">km/h</li>
            </ul>
          ) : readoutText ? (
            <p className="wind-bar__readout ds-text-subheadline" role="status" title={readoutText}>
              {readoutText}
            </p>
          ) : null}
          {windFailed ? (
            <p className="wind-bar__readout ds-text-subheadline" role="status">
              {t("wind.unavailable")}
            </p>
          ) : null}
          {precipText ? (
            <p
              className="wind-bar__readout wind-bar__readout--precip ds-text-subheadline"
              role="status"
              title={precipText}
            >
              {precipText}
            </p>
          ) : null}
          <div className="wind-bar__more" id="wind-more" ref={more}>
            {precip ? (
              <ul
                className="wind-bar__legend precip-legend ds-text-caption-1"
                aria-label={t("precip.legend")}
              >
                {([0, 1, 2, 3] as const).map((c) => (
                  <li key={c}>
                    <span
                      className="precip-legend__swatch"
                      aria-hidden="true"
                      style={{ background: `var(${precipVar(c)})` }}
                    />
                    {t(`precip.class${c}`)}
                  </li>
                ))}
                <li className="wind-bar__unit">mm/h</li>
              </ul>
            ) : null}
            <p className="wind-bar__model ds-text-caption-1">
              {[windOn ? t("wind.model") : null, precip ? t("precip.model") : null]
                .filter(Boolean)
                .join(" · ")}
              {runAt ? ` · ${t("wind.run", { time: formatRunTime(runAt, locale) })}` : ""}
            </p>
            <p className="wind-bar__model ds-text-caption-1">
              {t(both ? "precip.creditViaBoth" : windOn ? "wind.creditVia" : "precip.creditVia")}{" "}
              <a href={WIND_ATTRIBUTION_URL} target="_blank" rel="noopener noreferrer">
                Open-Meteo
              </a>{" "}
              (
              <a href={WIND_LICENCE_URL} target="_blank" rel="noopener noreferrer">
                CC BY 4.0
              </a>
              ), {t("wind.interpolated")}
            </p>
          </div>
        </>
      ) : (
        <p className="wind-bar__status ds-text-subheadline" role="status">
          {status === "loading"
            ? t(windOn ? "wind.loading" : "precip.loading")
            : t(windOn ? "wind.unavailable" : "precip.unavailable")}
        </p>
      )}
    </section>
  );
}
